/**
 * Canal entre o aparelho (`/linha`) e o console de supervisão.
 *
 * O aparelho publica o que acontece na conversa; o console publica comandos.
 * Nenhum dos dois fala com o outro diretamente — o backend é o cano.
 */

import { getToken } from './auth'
import { withBase } from './basePath'
import type { Artifact, ArtifactStatus, Effect, Speaker } from './types'

export type StreamRole = 'line' | 'supervisor'

/** Aparelho → console. A chamada nasce no aparelho; o console segue. */
export type LineMessage =
  | {
      type: 'dial'
      call_id: string
      case_id: string
      scenario_id: string
      case_name: string
      case_phone: string
      agent_name: string
    }
  | { type: 'answered'; at: string }
  | { type: 'declined' }
  | { type: 'ended'; at: string }
  | { type: 'connecting' }
  | { type: 'connected'; model: string; voice: string }
  | { type: 'failed'; reason: string }
  | { type: 'transcript'; speaker: Speaker; delta: string; at: string }
  | { type: 'turn_done'; speaker: Speaker }
  | { type: 'orb'; state: 'idle' | 'ai' | 'cli' | 'think' }
  | { type: 'effect'; effect: Effect; at: string }
  | {
      type: 'artifact'
      artifact: Artifact
      timeline: {
        delay_ms: number
        status: ArtifactStatus
        label: string
        progress_pct?: number
        progress_label?: string
      }[]
    }
  | { type: 'tick'; elapsed: number }

/** Console → aparelho. Só comandos: o console nunca origina a chamada. */
export type SupervisorMessage =
  | { type: 'command'; command: 'whisper'; text: string }
  | { type: 'command'; command: 'mute'; muted: boolean }
  | { type: 'command'; command: 'takeover'; human: boolean }
  | { type: 'command'; command: 'hangup' }

export type StreamMessage =
  | LineMessage
  | SupervisorMessage
  | { type: 'hello'; role: StreamRole; peers: Record<string, number> }
  | { type: 'peers'; peers: Record<string, number> }

export interface StreamHandle {
  send(message: LineMessage | SupervisorMessage): void
  close(): void
}

function endpoint(role: StreamRole): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  // WebSocket não carrega header de autorização: o token vai na query string.
  // Lido a cada (re)conexão, para pegar um login feito depois da tela abrir.
  const token = getToken()
  const query = token ? `?token=${encodeURIComponent(token)}` : ''
  return `${protocol}//${window.location.host}${withBase(`api/stream/${role}`)}${query}`
}

/**
 * Abre o canal e reconecta sozinho.
 *
 * A reconexão importa: numa demonstração ao vivo, um `--reload` do backend não
 * pode exigir que alguém recarregue as duas abas na frente do cliente.
 */
export function openStream(
  role: StreamRole,
  handlers: {
    onMessage(message: StreamMessage): void
    onStatusChange?(connected: boolean): void
  },
): StreamHandle {
  let socket: WebSocket | null = null
  let closed = false
  let retry = 0
  let retryTimer = 0

  const connect = () => {
    if (closed) return
    socket = new WebSocket(endpoint(role))

    socket.onopen = () => {
      retry = 0
      handlers.onStatusChange?.(true)
    }

    socket.onmessage = (event) => {
      try {
        handlers.onMessage(JSON.parse(event.data as string) as StreamMessage)
      } catch {
        // Mensagem malformada não derruba o canal.
      }
    }

    socket.onclose = () => {
      handlers.onStatusChange?.(false)
      if (closed) return
      retry += 1
      const delay = Math.min(500 * 2 ** (retry - 1), 5000)
      retryTimer = window.setTimeout(connect, delay)
    }

    socket.onerror = () => socket?.close()
  }

  connect()

  return {
    send(message) {
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message))
    },
    close() {
      closed = true
      window.clearTimeout(retryTimer)
      socket?.close()
    },
  }
}
